'use-client';
import React, { useRef } from 'react';
import classNames from 'clsx';
import directionStyles from '../../../common-styles/direction.module.scss';
import presetWrapperStyles from '../../../common-styles/presetWrapper.module.scss';
import styles from './styles/Button.module.scss';
import {
  getDataAttributes,
  getQaDataAttributes,
} from '../../../utils/dataUtils';
import { formatClassNames } from '../../../utils/classNames';
import type {
  StylableButtonSemanticClassNames,
  ButtonProps,
} from './Button.types';
import { IconAnimationTriggers } from './Button.types';
import AnimatedButtonIcon from './components/AnimatedButtonIcon';

export type { ButtonProps };
import { TestIds, SEMANTIC_CLASS_NAMES } from './constants';
import {
  useIconAnimation,
  useHoverState,
  useButtonEventHandlers,
  useButtonA11y,
} from './hooks';

const Button: React.FC<ButtonProps> = props => {
  const {
    id,
    link,
    type = 'button',
    label = '',
    disabled: isDisabled = false,
    onClick: propsOnClick,
    className,
    corvid,
    a11y: propsA11y,
    direction,
    elementProps,
    compPreviewState,
    isQaMode,
    fullNameCompType,
    lang,
    ariaAttributes,
    onDblClick,
    onFocus,
    onBlur,
    reportBiOnClick,
    preventLinkNavigation,
    onMouseEnter: propsOnMouseEnter,
    onMouseLeave: propsOnMouseLeave,
    wix,
  } = props;

  const presetsWrapperProps = wix?.presetsWrapperProps || {};

  const { iconAnimationTriggers = [IconAnimationTriggers.HOVER] } =
    corvid || {};

  const animatedIconProps = elementProps?.animatedIcon;

  const { iconAnimatedRef, onAnimationForward, onAnimationBackward } =
    useIconAnimation(isDisabled);
  const { active, onMouseEnter, onMouseLeave } = useHoverState(
    iconAnimationTriggers,
    onAnimationForward,
    onAnimationBackward,
    propsOnMouseEnter,
    propsOnMouseLeave,
  );

  const buttonOrLinkRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const semanticClassNames: StylableButtonSemanticClassNames =
    SEMANTIC_CLASS_NAMES;

  const rootClassName = classNames(
    styles.root,
    className,
    styles.button,
    'button',
    !direction && (directionStyles.fallbackDirection ?? styles.rootDirection),
    formatClassNames(semanticClassNames.root),
    {
      disabled: isDisabled,
      [styles.active]: active,
    },
  );

  const labelClassName = classNames(
    'label',
    styles.label,
    formatClassNames(semanticClassNames.buttonLabel),
  );

  const linkClassName = styles.link;

  const { isLink, eventHandlers } = useButtonEventHandlers({
    isDisabled,
    link,
    propsOnClick,
    preventLinkNavigation,
    reportBiOnClick,
    onMouseEnter,
    onMouseLeave,
    onDblClick,
    onFocus,
    onBlur,
  });

  const a11yAttr = useButtonA11y({ propsA11y, ariaAttributes, label });

  const wrapperAttributes = {
    id,
    ...getDataAttributes(props),
    ...getQaDataAttributes(isQaMode, fullNameCompType),
    'data-semantic-classname': semanticClassNames.root,
    lang,
    ...(direction && { dir: direction }),
  };

  const renderButtonContent = () => (
    <>
      {label && (
        <span className={labelClassName} data-testid={TestIds.buttonLabel}>
          {label}
        </span>
      )}
      <AnimatedButtonIcon
        ref={iconAnimatedRef}
        svg={animatedIconProps?.svg}
        duration={animatedIconProps?.duration}
      />
    </>
  );

  const renderButton = () => (
    <button
      type={type}
      disabled={isDisabled}
      className={rootClassName}
      data-testid={TestIds.buttonContent}
      {...wrapperAttributes}
      {...a11yAttr}
      {...eventHandlers}
      ref={buttonOrLinkRef as React.RefObject<HTMLButtonElement>}
      data-preview={compPreviewState}
    >
      {renderButtonContent()}
    </button>
  );

  const renderLinkedButton = () => {
    const linkHref = isDisabled ? undefined : link!.href;
    const linkRel = link!.rel;
    const linkTarget = link!.target;
    const linkPopupId = link!.linkPopupId;
    const anchorDataId = link!.anchorDataId;
    const anchorCompId = link!.anchorCompId;

    return (
      <a
        href={linkHref}
        rel={linkRel}
        target={linkTarget}
        data-popupid={linkPopupId}
        data-anchor={anchorDataId}
        data-anchor-comp-id={anchorCompId}
        className={classNames(linkClassName, rootClassName)}
        data-testid={TestIds.buttonContent}
        {...wrapperAttributes}
        {...a11yAttr}
        {...eventHandlers}
        role={a11yAttr.role}
        tabIndex={a11yAttr.tabIndex}
        ref={buttonOrLinkRef as React.RefObject<HTMLAnchorElement>}
        data-preview={compPreviewState}
      >
        {renderButtonContent()}
      </a>
    );
  };

  const renderDisabledLinkAsButton = () => (
    <button
      type={type}
      disabled={isDisabled}
      className={classNames(rootClassName)}
      data-testid={TestIds.buttonContent}
      {...wrapperAttributes}
      {...a11yAttr}
      {...eventHandlers}
      role="link"
      ref={buttonOrLinkRef as React.RefObject<HTMLButtonElement>}
      data-preview={compPreviewState}
    >
      {renderButtonContent()}
    </button>
  );

  const renderContent = () => {
    if (isDisabled && isLink) {
      return renderDisabledLinkAsButton();
    }
    return isLink ? renderLinkedButton() : renderButton();
  };

  return (
    <div className={presetWrapperStyles.presetWrapper} {...presetsWrapperProps}>
      {renderContent()}
    </div>
  );
};

export default Button;
